// src/components/panels/GraphDotMap.tsx
// The peek's one and only body: a true-silhouette dot map of the value graph
// on ghost geography. Dots sit at the graph's real layout positions projected
// into fixed zones that fill the panel; newly promoted nodes ping in place
// (ring + zone glow) — the map never swaps to another view. Near-zero text by
// design: whisper-faint zone labels, counts on zone hover, node titles only
// as dot tooltips. Clicking a zone (or any dot in it) focuses that region of
// the full graph.

import { useLayoutEffect, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import type { Edge, Node } from '@xyflow/react';
import { useCanvasData } from '@/hooks/use-canvas-data';
import { useSpineLayout } from '@/hooks/use-spine-layout';
import { useUIStore } from '@/stores';
import { deriveClusteredRawIds, deriveSynthesisEdges } from '@/services/synthesis-edges';
import type { NodeMetadata } from '@/services/canvas-mappers';
import { deriveZoneRects, projectDots } from '@/lib/dot-map-layout';
import { NODE_HUE } from '@/lib/node-visuals';
import {
  REGION_ORDER,
  REGION_SHORT_LABELS,
  deriveRegionCounts,
  nodeTypeToRegion,
  stepToRegion,
  type Region,
} from '@/lib/graph-regions';

const DOT_RADIUS: Record<string, number> = { idea: 5.5, customer: 4.5, capability: 4 };
const DEFAULT_RADIUS = 3.5;
// Past this many dots a zone steps its radii down one notch (never text).
const DENSE_ZONE_THRESHOLD = 40;

// Zone tints are deliberately muted/darker than the dot hues — territory, not content.
const ZONE_FILL: Record<Region, string> = {
  signals: NODE_HUE.market_signal,
  idea: '#78716c',
  capabilities: '#78716c',
  jtbd: '#78716c',
  assumptions: '#ca8a04',
};
const BASE_OPACITY = 0.04;
const STEP_OPACITY = 0.09; // standing emphasis on the current step's zone
const GLOW_OPACITY = 0.16; // arrival glow while the zone has pulsing dots
// Evidence overlay edges render dashed so the spine reads dominant.
const DASHED_EDGE_TYPES = new Set(['evidences', 'assumption-node']);

function useElementSize<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const rect = entries[0].contentRect;
      setSize({ width: rect.width, height: rect.height });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  return { ref, size };
}

export function GraphDotMap({
  projectId,
  pulseNodeIds,
}: {
  projectId: string;
  pulseNodeIds: Set<string>;
}) {
  const { data } = useCanvasData(projectId);
  const viewMode = useUIStore((s) => s.viewMode);
  const setViewMode = useUIStore((s) => s.setViewMode);
  const setGraphFocusRegion = useUIStore((s) => s.setGraphFocusRegion);
  const { ref, size } = useElementSize<HTMLDivElement>();
  const [hoverRegion, setHoverRegion] = useState<Region | null>(null);

  const allNodes: Node[] = useMemo(() => data?.nodes ?? [], [data?.nodes]);
  const allEdges: Edge[] = useMemo(() => data?.edges ?? [], [data?.edges]);

  // Synthesised primary view — same default as the full canvas: raw dialog
  // items already absorbed into a promoted theme are hidden.
  const nodes = useMemo(() => {
    const clustered = deriveClusteredRawIds(
      allNodes.map((n) => {
        const meta = n.data?.metadata as NodeMetadata | undefined;
        return { id: n.id, source: meta?.source, memberItemIds: meta?.memberItemIds };
      }),
    );
    return allNodes.filter((n) => {
      const meta = n.data?.metadata as NodeMetadata | undefined;
      return !(meta?.source === 'dialog' && clustered.has(n.id));
    });
  }, [allNodes]);

  // Stored edges + derived synthesis edges, restricted to visible endpoints.
  // Each carries its edgeType so evidence links can render dashed.
  const edges = useMemo(() => {
    const derived = deriveSynthesisEdges(
      allNodes.map((n) => {
        const meta = n.data?.metadata as NodeMetadata | undefined;
        return { id: n.id, source: meta?.source, memberItemIds: meta?.memberItemIds };
      }),
      allEdges.map((e) => ({
        source: e.source,
        target: e.target,
        edgeType: (e.data as { edgeType?: string } | undefined)?.edgeType ?? '',
      })),
    );
    const stored = allEdges.map((e) => ({
      id: e.id,
      source: e.source,
      target: e.target,
      edgeType: (e.data as { edgeType?: string } | undefined)?.edgeType ?? '',
    }));
    const visible = new Set(nodes.map((n) => n.id));
    return [...stored, ...derived].filter((e) => visible.has(e.source) && visible.has(e.target));
  }, [allNodes, allEdges, nodes]);

  // Same elk layout as the full canvas, so the silhouette matches reality.
  const layoutEdges = useMemo<Edge[]>(
    () => edges.map((e) => ({ id: e.id, source: e.source, target: e.target })),
    [edges],
  );
  const layoutPos = useSpineLayout(nodes, layoutEdges);

  const zones = useMemo(
    () => (size ? deriveZoneRects(size.width, size.height) : null),
    [size],
  );
  const dots = useMemo(
    () =>
      zones
        ? projectDots(
            nodes.map((n) => ({ id: n.id, type: n.type ?? '' })),
            layoutPos,
            zones,
          )
        : new Map<string, { x: number; y: number }>(),
    [nodes, layoutPos, zones],
  );

  const counts = useMemo(
    () => deriveRegionCounts(nodes.map((n) => ({ type: n.type ?? '' }))),
    [nodes],
  );
  const stepRegion = stepToRegion(viewMode);
  // Regions with a freshly-arrived (still pulsing) dot glow while it lands.
  const glowRegions = useMemo(() => {
    const glowing = new Set<Region>();
    for (const n of nodes) {
      if (!pulseNodeIds.has(n.id)) continue;
      const region = nodeTypeToRegion(n.type ?? '');
      if (region) glowing.add(region);
    }
    return glowing;
  }, [nodes, pulseNodeIds]);

  // Stable per-node stagger index so a batch of simultaneous arrivals still
  // ping in a few hundred ms apart (Set iteration order is insertion order).
  const pulseOrder = useMemo(
    () => new Map([...pulseNodeIds].map((id, i) => [id, i])),
    [pulseNodeIds],
  );

  // For the evidence-edge hover reveal: which territory each node lives in.
  const nodeRegionById = useMemo(() => {
    const m = new Map<string, Region | null>();
    for (const n of nodes) m.set(n.id, nodeTypeToRegion(n.type ?? ''));
    return m;
  }, [nodes]);

  const openInGraph = (region: Region) => {
    setGraphFocusRegion(region);
    setViewMode('graph');
  };
  const zoneKeyDown = (event: KeyboardEvent<SVGGElement>, region: Region) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      openInGraph(region);
    }
  };

  const radiusFor = (type: string, region: Region | null) => {
    const base = DOT_RADIUS[type] ?? DEFAULT_RADIUS;
    return region && counts[region] > DENSE_ZONE_THRESHOLD ? Math.max(2.5, base - 1) : base;
  };

  return (
    <div ref={ref} className="h-full w-full">
      {size && zones && (
        <svg width={size.width} height={size.height} role="group" aria-label="Map of the value graph">
          {REGION_ORDER.map((region) => {
            const z = zones[region];
            const isStep = region === stepRegion;
            const glowing = glowRegions.has(region);
            // The current step's territory wears the app's active colour
            // (primary tint + hairline outline) — a darker grey read as
            // shading, not "you are here". The arrival glow still wins the
            // fill while dots land; the outline stays put throughout.
            const fill = !glowing && isStep ? 'hsl(var(--primary))' : ZONE_FILL[region];
            const base = glowing ? GLOW_OPACITY : isStep ? STEP_OPACITY : BASE_OPACITY;
            const opacity = hoverRegion === region ? Math.min(base + 0.05, 0.2) : base;
            return (
              <g
                key={region}
                className="vg-map-zone cursor-pointer"
                role="button"
                tabIndex={0}
                aria-label={`Open ${REGION_SHORT_LABELS[region]} in the full graph`}
                onClick={() => openInGraph(region)}
                onKeyDown={(e) => zoneKeyDown(e, region)}
                onMouseEnter={() => setHoverRegion(region)}
                onMouseLeave={() => setHoverRegion(null)}
              >
                <rect
                  className="vg-map-zone-rect"
                  x={z.x}
                  y={z.y}
                  width={z.width}
                  height={z.height}
                  rx={8}
                  fill={fill}
                  fillOpacity={opacity}
                  stroke={isStep ? 'hsl(var(--primary))' : undefined}
                  strokeOpacity={isStep ? 0.45 : undefined}
                  strokeWidth={isStep ? 1 : undefined}
                />
                {/* Count sits on its own line under the label: inline it bled
                    past narrow zones, right-anchored it collided with long
                    labels. A bare number always fits the zone width. */}
                <text
                  className="vg-map-zone-label"
                  x={z.x + 8}
                  y={z.y + 14}
                  style={isStep ? { opacity: 1 } : undefined}
                >
                  {REGION_SHORT_LABELS[region].toUpperCase()}
                  <tspan className="vg-map-zone-count" x={z.x + 8} dy={12}>
                    {counts[region]}
                  </tspan>
                </text>
              </g>
            );
          })}
          {edges.map((e) => {
            const s = dots.get(e.source);
            const t = dots.get(e.target);
            if (!s || !t) return null;
            const sourcePulse = pulseNodeIds.has(e.source) ? pulseOrder.get(e.source) ?? 0 : -1;
            const targetPulse = pulseNodeIds.has(e.target) ? pulseOrder.get(e.target) ?? 0 : -1;
            const fresh = sourcePulse >= 0 || targetPulse >= 0;
            const delay = Math.max(sourcePulse, targetPulse, 0) * 250;
            const dashed = DASHED_EDGE_TYPES.has(e.edgeType);
            // Evidence edges are invisible at rest — at real graph sizes even
            // a whisper of dozens of dashed lines drowned the spine. They
            // show while their node arrives (the flash), and on demand while
            // either endpoint's territory is hovered.
            const revealed =
              dashed &&
              hoverRegion !== null &&
              (nodeRegionById.get(e.source) === hoverRegion ||
                nodeRegionById.get(e.target) === hoverRegion);
            const restOpacity = dashed ? (revealed ? 0.7 : 0) : 0.7;
            return (
              <line
                key={e.id}
                className={fresh ? 'vg-map-edge vg-map-edge-in' : 'vg-map-edge'}
                style={fresh ? { animationDelay: `${delay}ms` } : undefined}
                x1={s.x}
                y1={s.y}
                x2={t.x}
                y2={t.y}
                // Evidence edges only ever show as flash or hover-reveal, so
                // they can afford real ink; spine edges stay a shade lighter.
                stroke={dashed ? '#78716c' : '#a8a29e'}
                strokeWidth={1}
                strokeDasharray={dashed ? '2 2' : undefined}
                opacity={fresh ? 0.9 : restOpacity}
              />
            );
          })}
          {nodes.map((n) => {
            const p = dots.get(n.id);
            if (!p) return null;
            const type = n.type ?? '';
            const region = nodeTypeToRegion(type);
            const r = radiusFor(type, region);
            const fill = NODE_HUE[type] ?? '#a8a29e';
            const pulsing = pulseNodeIds.has(n.id);
            const title = (n.data?.label as string) ?? '';
            const pulseDelay = pulsing
              ? { animationDelay: `${(pulseOrder.get(n.id) ?? 0) * 250}ms` }
              : undefined;
            return (
              <g
                key={n.id}
                className="cursor-pointer"
                onClick={() => region && openInGraph(region)}
              >
                {pulsing && (
                  <circle
                    className="vg-map-ring"
                    style={pulseDelay}
                    cx={p.x}
                    cy={p.y}
                    r={r + 2}
                    fill="none"
                    stroke={fill}
                    strokeWidth={1.5}
                  />
                )}
                <circle
                  className={pulsing ? 'vg-map-dot vg-map-dot-in' : 'vg-map-dot'}
                  style={pulseDelay}
                  cx={p.x}
                  cy={p.y}
                  r={r}
                  fill={fill}
                >
                  <title>{title}</title>
                </circle>
              </g>
            );
          })}
        </svg>
      )}
    </div>
  );
}
